import * as THREE from "three";
import {
  Direction,
  C3D_CELL,
  C3D_ATTACK_CELL,
  C3D_COLS,
  C3D_IDLE_MS,
  C3D_WALK_MS,
  C3D_RUN_MS,
  C3D_IDLE_FRAMES,
  C3D_WALK_FRAMES,
  C3D_RUN_FRAMES,
  c3dIdleFrame,
  c3dWalkFrame,
  c3dRunFrame,
  C3D_ATTACK_FRAMES,
  C3D_ATTACK_COLS,
  C3D_ATTACK_MS,
  C3D_SMOKE_FRAMES,
  C3D_SMOKE_COLS,
  C3D_SMOKE_MS,
  C3D_SIT_COLS,
  C3D_JUMP_COLS,
  appearanceHash,
  type CharacterAppearance,
} from "@ghostland/shared";
import { compositeAtlas, compositeAttackAtlas, compositeBlockAtlas, composeSmokeAtlas, composeSitAtlas, composeJumpAtlas } from "../sprites/c3dPipeline.js";
import { buildBmx, type BmxProp } from "./bmxProp.js";
import { dayNight } from "./dayNight.js";
import { asOverheadUI } from "./overheadLayer.js";
import { assetUrl } from "../assetVer.js";

/**
 * Cross-instance cache of composited idle/walk/run atlas CANVASES, keyed by appearance hash, used only
 * for COMBATLESS billboards (ambient NPCs / townsfolk / customers). The paperdoll composite is the
 * expensive part (~5MP, several per-pixel recolour passes); identical looks now composite ONCE and every
 * instance just makes a cheap CanvasTexture from the shared canvas. Bounded by the number of distinct NPC
 * looks (small — townsfolk use deterministic seeds), so it never grows unbounded. Players are unaffected
 * (they pass through the per-instance equip-state path, since their atlas varies with held gear).
 */
const sharedNpcAtlas = new Map<string, Promise<HTMLCanvasElement | null>>();

/**
 * A character rendered the way the spike proved out: the hand-tuned pre-rendered
 * c3d sprite (body + hair composited into one atlas) billboarded into the 3D world.
 * Body and hair are flattened into a single 20x8 atlas canvas (the diff-extracted
 * hair already occludes the face correctly in 3D), then we page through cells by
 * UV offset — idle 6fps / walk 12fps, row chosen by facing relative to the camera.
 */

/** World heading (atan2(x,z)) for each 8-way facing, matching local movement math. */
const DIR_ANGLE: Record<string, number> = {
  "down": 0,
  "down-right": Math.PI / 4,
  "right": Math.PI / 2,
  "up-right": (3 * Math.PI) / 4,
  "up": Math.PI,
  "up-left": -(3 * Math.PI) / 4,
  "left": -Math.PI / 2,
  "down-left": -Math.PI / 4,
};

const CHAR_H = 2.2;     // world height of the sprite plane
const FOOT_FRAC = 0.58; // where the feet sit within the cell, top-down
const CHAR_HIT_MS = 300; // duration of the flash + shake
const HEALTH_BAR_SHOW_MS = 5000; // keep the floating health bar up for 5s after the last hit, then hide
const HIT_RED: readonly [number, number, number] = [1, 0.28, 0.28];   // damage flash (peak tint)
const HIT_BLUE: readonly [number, number, number] = [0.3, 0.62, 1];   // blocked/stagger flash (peak tint)
// The attack sheet uses a larger cell (room for the sword swing). While the attack plays we scale the
// sprite quad by this so the figure stays the SAME on-screen size (the figure is the same px, the cell
// just has margin), and lift it so the feet stay on the ground (feet are at FOOT_FRAC of the big cell).
const ATTACK_SCALE = C3D_ATTACK_CELL / C3D_CELL;

export class CharBillboard {
  readonly group = new THREE.Group();
  private readonly sprite: THREE.Mesh;
  private readonly mat: THREE.MeshBasicMaterial;
  private tex: THREE.CanvasTexture | null = null;

  private gen = 0;
  /** Resolves the first time a texture is actually applied — so callers can wait for the billboard
   *  to be drawn (not white) regardless of which (possibly superseded) setAppearance call wins. */
  private texturedResolve: (() => void) | null = null;
  readonly textured = new Promise<void>((r) => { this.texturedResolve = r; });
  private facing: Direction = Direction.Down;
  private moving = false;
  private running = false;
  private motionKey = 0; // 0 idle, 1 walk, 2 run — frame/clock reset when it changes
  private clock = 0;
  private phase = 0;
  /** World heading used for picking the facing row; set from movement or `dir`. */
  faceAngle = 0;

  private readonly baseSpriteY = CHAR_H * (FOOT_FRAC - 0.5);
  private attackTex: THREE.Texture | null = null; // this billboard's clone of the shared attack sheet
  private lastAppearance: CharacterAppearance | null = null; // for rebuilding the attack atlas on equip change
  private equipWeapon = false; // show/bake the sword layer only when a weapon is held + drawn (combat)
  private equipShield = false; // show/bake the shield layer only when an offhand shield is held + drawn
  private attacking = false;
  private attackPhase = 0;
  private attackClock = 0;
  private blockTex: THREE.Texture | null = null; // static raised-shield block stance (1 col x 8 dir)
  private blocking = false;
  private sitTex: THREE.Texture | null = null; // static seated pose (1 col x 8 dir); null until art loads
  private sitting = false;
  private jumpTex: THREE.Texture | null = null; // static airborne pose (1 col x 8 dir); null until art loads
  private jumping = false;
  private hopY = 0; // current jump-hop vertical offset of the body sprite (the shadow stays grounded)
  private lastCamYaw = 0; // last yaw seen in tick — lets playAttack pick the facing row immediately
  private hitClock = 0;   // ms remaining of the flash + shake (0 = none)
  private hitTint: readonly [number, number, number] = HIT_RED; // peak colour of the active flash
  // Smoke override: a looping standing-smoke sheet that replaces the idle pose while a Blunt buff is
  // active. One shared base-character sheet (NOT appearance-composited), loaded lazily per billboard.
  private smokeTex: THREE.Texture | null = null;
  private smoking = false;
  private smokePhase = 0;
  private smokeClock = 0;
  private dead = false;
  // Per-equip-state atlas caches — drawing the sword/shield (combat toggle) is a heavy recomposite, so
  // we build each (weapon,shield) combo ONCE and just swap the cached texture on toggle (fixes the lag).
  private readonly mainCache = new Map<string, THREE.CanvasTexture>();
  private readonly attackCache = new Map<string, THREE.Texture>();
  private readonly blockCache = new Map<string, THREE.Texture>();
  private readonly sitCache = new Map<string, THREE.Texture>();
  private readonly jumpCache = new Map<string, THREE.Texture>();
  private smokeBuilt = false; // smoke atlas depends only on appearance (not equip) → build once
  private healthBar: THREE.Sprite | null = null;
  private healthTex: THREE.CanvasTexture | null = null;
  private healthCanvas: HTMLCanvasElement | null = null;
  private lastHp = -1;
  private healthHideIn = 0; // ms left to keep the health bar visible; armed on each hit, hides at 0
  private skullSprite: THREE.Sprite | null = null; // "red-named" skull marker above the head (shared texture)
  private rodProp: THREE.Group | null = null; // fishing rod prop (idle/charge/cast/hooked stances)
  private bmx: BmxProp | null = null;         // the BMX under the rider (lazily built, shown while riding)
  private bmxSpeed = 0;                       // rider ground speed (world units/s) — spins the wheels
  private fishState = 0;                      // 0 none · 1 hold · 2 charging · 3 cast · 4 hooked
  private rodClock = 0;                       // ms into the current rod stance (drives its animation)
  private readonly prewarming = new Set<string>(); // equip-states currently being pre-built in the background
  private fadeClock = 0; private fadeDur = 0; // smooth opacity fade-in (e.g. after a teleport/zone change)
  // Ambient NPCs never fight/smoke. In combatless mode we skip the attack/block/smoke atlas builds and
  // share the composited main atlas across instances (by appearance hash) — the spawn becomes ~1 cheap
  // texture upload instead of 4 full ~5MP composites, which is what was hitching the frame per spawn.
  private readonly combatless: boolean;

  constructor(opts?: { combatless?: boolean }) {
    this.combatless = !!opts?.combatless;
    this.mat = new THREE.MeshBasicMaterial({ transparent: true, alphaTest: 0.5, depthWrite: true });
    this.sprite = new THREE.Mesh(new THREE.PlaneGeometry(CHAR_H, CHAR_H), this.mat);
    this.sprite.position.y = CHAR_H * (FOOT_FRAC - 0.5);
    // Hidden until the first composited atlas lands — compositing is async (off-thread), and an
    // untextured MeshBasicMaterial would render as a solid white quad in the meantime.
    this.sprite.visible = false;
    this.group.add(this.sprite);

    const blob = new THREE.Mesh(
      new THREE.CircleGeometry(0.42, 24),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.28, depthWrite: false }),
    );
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = 0.02;
    blob.raycast = () => {}; // the ground shadow must never catch clicks (only the body pixels do)
    this.group.add(blob);
  }

  /** Jump hop: raise ONLY the body sprite (not the whole group), so the ground shadow + name/health
   *  (which live in the group at ground level) stay planted while the character lifts off. dy = 0 grounds
   *  it. Accounts for the attack-swing scale so the two never fight. */
  setHop(dy: number): void {
    this.hopY = dy;
    this.sprite.position.y = this.baseSpriteY * (this.attacking ? ATTACK_SCALE : 1) + dy;
  }

  /** Show a floating health bar above the head (hidden at full health or while downed). */
  setHealth(h: number, max: number): void {
    if (!this.healthBar) {
      const c = document.createElement("canvas"); c.width = 40; c.height = 6;
      const tex = new THREE.CanvasTexture(c); tex.magFilter = tex.minFilter = THREE.NearestFilter;
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
      sp.scale.set(0.95, 0.14, 1); sp.position.set(0, 1.82, 0); sp.renderOrder = 998;
      asOverheadUI(sp); // crisp overlay layer — never softened by the world depth-of-field
      this.group.add(sp);
      this.healthBar = sp; this.healthTex = tex; this.healthCanvas = c;
    }
    const pct = max > 0 ? Math.max(0, Math.min(1, h / max)) : 0;
    if (h < this.lastHp - 0.5) this.healthHideIn = HEALTH_BAR_SHOW_MS; // took a hit → (re)arm the 5s window
    this.healthBar.visible = !this.dead && h > 0 && h < max && this.healthHideIn > 0;
    if (Math.abs(h - this.lastHp) < 0.5 || !this.healthBar.visible) { this.lastHp = h; return; }
    this.lastHp = h;
    const ctx = this.healthCanvas!.getContext("2d")!; const W = 40, H = 6;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#05000a"; ctx.fillRect(0, 0, W, H);                 // backdrop/border
    ctx.fillStyle = "#2a1018"; ctx.fillRect(1, 1, W - 2, H - 2);          // empty track
    ctx.fillStyle = pct > 0.5 ? "#5ad06a" : pct > 0.25 ? "#e0c24a" : "#e05a5a";
    ctx.fillRect(1, 1, Math.round((W - 2) * pct), H - 2);                 // fill
    this.healthTex!.needsUpdate = true;
  }

  /** Toggle the skeleton/skull marker floating above the head — shown for "red-named" (negative
   *  affinity) players so everyone can see who's flagged for PvP. Built lazily the first time it shows. */
  setSkeleton(on: boolean): void {
    if (!on) { if (this.skullSprite) this.skullSprite.visible = false; return; }
    if (!this.skullSprite) {
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: skullTexture(), transparent: true, depthTest: false }));
      sp.scale.set(0.55, 0.55, 1); sp.position.set(0, 2.12, 0); sp.renderOrder = 999; // just above the health bar
      asOverheadUI(sp); // crisp overlay layer
      this.group.add(sp);
      this.skullSprite = sp;
    }
    this.skullSprite.visible = true;
  }

  /** FISHING ROD prop states: 0 = none · 1 = held idle at the side · 2 = CHARGING (rod swung back
   *  over the shoulder, winding deeper the longer it charges) · 3 = line CAST (rod forward over the
   *  water, gentle bob) · 4 = HOOKED (rod bent, straining shake). A lightweight 3D prop (thin dark
   *  rod + a paler tip) attached to the billboard group — no sprite re-render needed, and it plays
   *  on remotes exactly like the local player via the synced fishState. */
  setFishState(state: number): void {
    if (state === this.fishState) return;
    this.fishState = state;
    if (state <= 0) { if (this.rodProp) this.rodProp.visible = false; return; }
    if (!this.rodProp) {
      const g = new THREE.Group();
      const shaft = new THREE.Mesh(
        new THREE.CylinderGeometry(0.016, 0.028, 1.35, 5),
        new THREE.MeshBasicMaterial({ color: 0x4a3824 }),
      );
      shaft.position.y = 0.675; g.add(shaft);
      const tip = new THREE.Mesh(
        new THREE.CylinderGeometry(0.010, 0.016, 0.45, 5),
        new THREE.MeshBasicMaterial({ color: 0xc8b890 }),
      );
      tip.position.y = 1.35 + 0.225 - 0.02; g.add(tip);
      const reel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.055, 0.055, 0.03, 8),
        new THREE.MeshBasicMaterial({ color: 0x2c2c30 }),
      );
      reel.rotation.x = Math.PI / 2; reel.position.set(0.02, 0.28, 0.05); g.add(reel);
      g.position.set(0.42, 0.55, 0.10); // placeholder — tickRod re-anchors it to the character's right
      this.group.add(g);
      this.rodProp = g;
    }
    this.rodProp.visible = true;
    this.rodClock = 0;
  }

  private static readonly ROD_SIDE = 0.22; // rod anchor: offset to the character's right
  private static readonly ROD_FWD = 0.10;  // ...and slightly ahead of them

  /** Per-frame rod-prop posing (called from tick): each state is a small transform animation.
   *  Rotations are yaw-aligned to the CHARACTER'S FACING (order YXZ, y = faceAngle — the world
   *  heading the sprite row is picked from), so the wind-up swings behind the CHARACTER and the
   *  cast points out over the water they face, no matter where the camera orbits. Pitch sign:
   *  positive tips the rod toward the facing (front), negative behind. */
  private tickRod(dtMs: number, _camYaw: number): void {
    if (!this.rodProp || !this.rodProp.visible) return;
    this.rodClock += dtMs;
    const t = this.rodClock / 1000;
    const r = this.rodProp;
    r.rotation.order = "YXZ";
    // anchor the rod at hand height, offset to the CHARACTER'S right (facing-relative, not world x)
    const fwdX = Math.sin(this.faceAngle), fwdZ = Math.cos(this.faceAngle);
    const RS = CharBillboard.ROD_SIDE, RF = CharBillboard.ROD_FWD;
    r.position.set(-fwdZ * RS + fwdX * RF, 0.55, fwdX * RS + fwdZ * RF);
    if (this.fishState === 1) {
      r.rotation.set(0.25, this.faceAngle, 0.30 + Math.sin(t * 1.2) * 0.02); // resting at the side, idle sway
    } else if (this.fishState === 2) {
      const wind = Math.min(1, this.rodClock / 1400);
      // wind-up: the rod starts NEAR the body's angle and swings back modestly as the charge deepens
      r.rotation.set(-(0.5 + wind * 0.55 + Math.sin(t * 14) * 0.03 * wind), this.faceAngle, 0.08); // near-upright (remotes see this)
    } else if (this.fishState === 3) {
      r.rotation.set(0.9 + Math.sin(t * 1.6) * 0.05, this.faceAngle, 0.45); // out over the water, leaning right
    } else if (this.fishState === 4) {
      r.rotation.set(0.7 + Math.sin(t * 18) * 0.09, this.faceAngle, Math.sin(t * 13) * 0.06); // bent + fighting shake
    }
  }

  /** Smoothly fade the figure in (opacity 0→1 over `ms`). Used when a remote (re)appears after a
   *  teleport / zone change so they don't snap-pop or streak across the map. alphaTest is dropped to a
   *  TINY value (not 0) during the fade: the figure still alpha-blends in smoothly, but genuinely
   *  transparent texels are still discarded — otherwise the whole quad renders and the ground (green
   *  grass) shows through as a coloured box. Restored to 0.5 when the fade finishes. */
  fadeIn(ms = 380): void {
    this.fadeClock = ms; this.fadeDur = ms;
    this.mat.opacity = 0; this.mat.alphaTest = 0.02;
  }

  /** Trigger the "got hit" feedback: a brief RED flash + a quick shake. Call on each damaging hit. */
  hit(): void { if (!this.dead) { this.hitClock = CHAR_HIT_MS; this.hitTint = HIT_RED; } }

  /** Trigger the "blocked / staggered" feedback: a brief BLUE flash + shake (your attack was blocked). */
  stagger(): void { if (!this.dead) { this.hitClock = CHAR_HIT_MS; this.hitTint = HIT_BLUE; } }

  /** Advance + apply the flash (tint→normal colour multiply) and shake (decaying horizontal jitter).
   *  Self-contained: leaves colour white + sprite re-centred once it elapses. */
  private applyHitFx(dtMs: number): void {
    if (this.hitClock <= 0) return;
    this.hitClock = Math.max(0, this.hitClock - dtMs);
    const t = this.hitClock / CHAR_HIT_MS;                 // 1 → 0 over the flash
    const [tr, tg, tb] = this.hitTint;                     // peak tint at t=1, white at t=0
    this.mat.color.setRGB(1 - (1 - tr) * t, 1 - (1 - tg) * t, 1 - (1 - tb) * t);
    if (!this.attacking) this.sprite.position.x = Math.sin(this.hitClock * 0.07) * 0.09 * t; // shake
    if (this.hitClock === 0) { this.mat.color.setRGB(1, 1, 1); if (!this.attacking) this.sprite.position.x = 0; }
  }

  /** Lay the character flat on the ground (downed) or stand them back up. */
  setDead(dead: boolean): void {
    if (dead === this.dead) return;
    this.dead = dead;
    if (dead) {
      this.sprite.rotation.x = -Math.PI / 2; // flat onto the ground
      this.sprite.position.set(0, 0.06, 0.35);
      if (this.healthBar) this.healthBar.visible = false;
    } else {
      this.sprite.rotation.x = 0;
      this.sprite.position.set(0, this.baseSpriteY, 0);
    }
  }

  /**
   * Pixel-accurate hit test: is the sprite's CURRENT frame opaque at the plane-UV `(u,v)`
   * from a raycast? Clicks should only land on the character's actual body pixels — not the
   * transparent corners of the billboard quad. Falls back to `true` if the atlas can't be read.
   */
  hitTestUV(uv: THREE.Vector2): boolean {
    const canvas = this.tex?.image as HTMLCanvasElement | undefined;
    if (!canvas || !this.tex) return true;
    const tu = this.tex.offset.x + uv.x * this.tex.repeat.x;
    const tv = this.tex.offset.y + uv.y * this.tex.repeat.y;
    const x = Math.floor(tu * canvas.width);
    const y = Math.floor((1 - tv) * canvas.height); // canvas y is top-down; texture v is bottom-up
    if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) return false;
    try {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      return ctx ? ctx.getImageData(x, y, 1, 1).data[3] > 64 : true;
    } catch {
      return true; // tainted canvas — fall back to the quad
    }
  }

  private equipKey(): string { return `${this.equipWeapon ? 1 : 0}${this.equipShield ? 1 : 0}`; }

  /** Drop the cached atlases (a new appearance invalidates every equip-state). */
  private clearAtlasCache(): void {
    for (const t of this.mainCache.values()) t.dispose();
    for (const t of this.attackCache.values()) t.dispose();
    for (const t of this.blockCache.values()) t.dispose();
    for (const t of this.sitCache.values()) t.dispose();
    for (const t of this.jumpCache.values()) t.dispose();
    this.mainCache.clear(); this.attackCache.clear(); this.blockCache.clear(); this.sitCache.clear(); this.jumpCache.clear();
    this.sitTex = null;
    this.jumpTex = null;
    this.smokeBuilt = false;
  }

  async setAppearance(a: CharacterAppearance): Promise<void> {
    this.lastAppearance = a;
    this.clearAtlasCache(); // appearance changed → previously-composited atlases are stale
    await this.rebuildMainAtlas(++this.gen);
    if (this.combatless) return; // ambient NPC: no attack swing / block stance / smoke to pre-build
    // Build this character's attack-swing atlas in the background (skin-remapped body + clothes +
    // only the equipped sword/shield). Ready by the time they press attack; until then playAttack no-ops.
    this.buildAttackAtlas();
    this.buildBlockAtlas();
    if (this.sitting) this.buildSitAtlas(); // keep the seated pose through an appearance change
    if (this.jumping) this.buildJumpAtlas(); // keep the airborne pose through an appearance change
  }

  /** (Re)composite the idle/walk/run atlas — body + clothing + hair + the held sword/shield (when in
   *  combat, gated by equipWeapon/equipShield) — and rebuild the smoke atlas from it. */
  private async rebuildMainAtlas(gen: number): Promise<void> {
    const a = this.lastAppearance;
    if (!a) return;
    // Reuse the cached atlas for this equip-state if we've already built it (instant combat toggle).
    const key = this.equipKey();
    let tex = this.mainCache.get(key);
    if (!tex) {
      // Combatless NPCs reuse a shared composited canvas per look (composite once across all instances);
      // players composite their own (atlas varies with held gear).
      let canvas: HTMLCanvasElement | null;
      if (this.combatless) {
        const h = appearanceHash(a);
        let pending = sharedNpcAtlas.get(h);
        if (!pending) { pending = compositeAtlas(a, false, false); sharedNpcAtlas.set(h, pending); }
        canvas = await pending;
      } else {
        canvas = await compositeAtlas(a, this.equipWeapon, this.equipShield);
      }
      if (gen !== this.gen || !canvas) return; // superseded or failed
      tex = new THREE.CanvasTexture(canvas);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.repeat.set(1 / C3D_COLS, 1 / (canvas.height / C3D_CELL));
      this.mainCache.set(key, tex);
      // The standing-smoke atlas depends only on appearance (not equip) → build it once (NPCs never smoke).
      if (!this.combatless && !this.smokeBuilt) { this.smokeBuilt = true; this.buildSmokeAtlas(canvas); }
    } else if (gen !== this.gen) {
      return; // a newer rebuild superseded this one
    }
    this.tex = tex;
    if (!this.attacking) { this.mat.map = tex; this.mat.needsUpdate = true; } // don't interrupt a swing
    this.applyFrame();
    this.sprite.visible = true; // first atlas is in — safe to show (no white-quad flash)
    this.texturedResolve?.(); // mark "drawn at least once" for boot-reveal gating
    this.texturedResolve = null;
  }

  /** Tell the billboard which hands are equipped+drawn (sword/shield), so the held layer appears in
   *  idle/walk/run AND the attack swing bakes ONLY those gears. Rebuilds both atlases on change. */
  setEquippedHands(weapon: boolean, shield: boolean): void {
    if (weapon === this.equipWeapon && shield === this.equipShield) return;
    this.equipWeapon = weapon;
    this.equipShield = shield;
    if (this.lastAppearance) {
      void this.rebuildMainAtlas(++this.gen); // held sword/shield in idle/walk/run
      this.buildAttackAtlas();                // + re-bake the swing
      this.buildBlockAtlas();                 // + re-bake the block stance
      if (this.sitting) this.buildSitAtlas(); // + re-bake the seated pose
    }
  }

  /** Show/hide the BMX under this character (built lazily on first ride). Callers pair this with
   *  setSitting(true) + setHop(BMX_SIT_Y) so the rider perches on the saddle. */
  setBmx(on: boolean): void {
    if (on && !this.bmx) {
      this.bmx = buildBmx();
      this.group.add(this.bmx.group);
    }
    if (this.bmx) this.bmx.group.visible = on;
    if (!on) this.bmxSpeed = 0;
  }

  /** Rider ground speed (world units/s) — drives the wheel + crank spin. */
  setBmxSpeed(speed: number): void {
    this.bmxSpeed = Math.max(0, speed);
  }

  /** Toggle the held block stance (raised shield). While on, the static block sheet replaces the
   *  normal billboard until released (attack overrides it). */
  setBlocking(on: boolean): void {
    if (on === this.blocking) return;
    this.blocking = on;
    if (!on && this.tex && this.mat.map === this.blockTex) {
      this.mat.map = this.tex; this.mat.needsUpdate = true; // restore the normal atlas
    }
  }

  /** Toggle the seated sit pose. While on, the static sit sheet replaces the normal billboard
   *  (attack/block override it). No-op visual if the sit art isn't generated yet (sitTex stays null,
   *  so the billboard keeps the perched idle pose). */
  setSitting(on: boolean): void {
    if (on === this.sitting) return;
    this.sitting = on;
    if (on) this.buildSitAtlas();
    else if (this.tex && this.mat.map === this.sitTex) {
      this.mat.map = this.tex; this.mat.needsUpdate = true; // restore the normal atlas
    }
  }

  private buildSitAtlas(): void {
    const a = this.lastAppearance;
    if (!a) return;
    const key = this.equipKey();
    const cached = this.sitCache.get(key);
    if (cached) { this.sitTex = cached; return; }
    const gen = this.gen;
    void composeSitAtlas(a, this.equipWeapon, this.equipShield).then((c) => {
      if (gen !== this.gen || !c) return; // null = sit art not generated yet → keep idle pose
      const st = new THREE.CanvasTexture(c);
      st.magFilter = THREE.NearestFilter; st.minFilter = THREE.NearestFilter;
      st.colorSpace = THREE.SRGBColorSpace;
      st.repeat.set(1 / C3D_SIT_COLS, 1 / 8); // 1 col x 8 dir-rows
      this.sitCache.set(key, st);
      if (this.equipKey() === key) this.sitTex = st;
    });
  }

  /** Toggle the airborne jump pose. While on, the static jump sheet replaces the normal billboard
   *  (attack/block override it). No-op visual until the jump art loads (jumpTex stays null → normal pose). */
  setJumping(on: boolean): void {
    if (on === this.jumping) return;
    this.jumping = on;
    if (on) this.buildJumpAtlas();
    else if (this.tex && this.mat.map === this.jumpTex) {
      this.mat.map = this.tex; this.mat.needsUpdate = true; // restore the normal atlas
    }
  }

  private buildJumpAtlas(): void {
    const a = this.lastAppearance;
    if (!a) return;
    const key = this.equipKey();
    const cached = this.jumpCache.get(key);
    if (cached) { this.jumpTex = cached; return; }
    const gen = this.gen;
    void composeJumpAtlas(a, this.equipWeapon, this.equipShield).then((c) => {
      if (gen !== this.gen || !c) return; // null = jump art not generated yet → keep normal pose
      const jt = new THREE.CanvasTexture(c);
      jt.magFilter = THREE.NearestFilter; jt.minFilter = THREE.NearestFilter;
      jt.colorSpace = THREE.SRGBColorSpace;
      jt.repeat.set(1 / C3D_JUMP_COLS, 1 / 8); // 1 col x 8 dir-rows
      this.jumpCache.set(key, jt);
      if (this.equipKey() === key) this.jumpTex = jt;
    });
  }

  private buildBlockAtlas(): void {
    const a = this.lastAppearance;
    if (!a) return;
    const key = this.equipKey();
    const cached = this.blockCache.get(key);
    if (cached) { this.blockTex = cached; return; } // already built for this equip-state
    const gen = this.gen;
    void compositeBlockAtlas(a, this.equipWeapon, this.equipShield).then((c) => {
      if (gen !== this.gen || !c) return;
      const bt = new THREE.CanvasTexture(c);
      bt.magFilter = THREE.NearestFilter; bt.minFilter = THREE.NearestFilter;
      bt.colorSpace = THREE.SRGBColorSpace;
      bt.repeat.set(1, 1 / 8); // 1 col x 8 dir-rows
      this.blockCache.set(key, bt);
      if (this.equipKey() === key) this.blockTex = bt;
    });
  }

  private buildAttackAtlas(): void {
    const a = this.lastAppearance;
    if (!a) return;
    const key = this.equipKey();
    const cached = this.attackCache.get(key);
    if (cached) { this.attackTex = cached; return; } // already built for this equip-state
    const gen = this.gen;
    void compositeAttackAtlas(a, this.equipWeapon, this.equipShield).then((c) => {
      if (gen !== this.gen || !c) return;
      const at = new THREE.CanvasTexture(c);
      at.magFilter = THREE.NearestFilter; at.minFilter = THREE.NearestFilter;
      at.colorSpace = THREE.SRGBColorSpace;
      at.repeat.set(1 / C3D_ATTACK_COLS, 1 / 8);
      this.attackCache.set(key, at);
      if (this.equipKey() === key) this.attackTex = at;
    });
  }

  /** Pre-build (in the BACKGROUND, during idle) the idle/walk + attack + block atlases for an
   *  equip-state the character will likely switch to (e.g. combat with sword/shield), so drawing the
   *  weapon is an instant cache hit instead of three heavy composites on one frame (fixes the toggle
   *  lag). No-op if it's the active state, already cached, or already building. The three composites
   *  are chained (not fired at once) so they spread across frames. */
  prewarmEquip(weapon: boolean, shield: boolean): void {
    const a = this.lastAppearance;
    if (!a) return;
    const key = `${weapon ? 1 : 0}${shield ? 1 : 0}`;
    if (this.equipKey() === key) return;                                   // already the active state
    if (this.mainCache.has(key) || this.prewarming.has(key)) return;       // built or in flight
    this.prewarming.add(key);
    const gen = this.gen;
    const work = (): void => {
      void Promise.resolve()
        .then(() => (this.mainCache.has(key) ? null : compositeAtlas(a, weapon, shield)))
        .then((canvas) => {
          if (gen !== this.gen || !canvas || this.mainCache.has(key)) return;
          const t = new THREE.CanvasTexture(canvas);
          t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
          t.repeat.set(1 / C3D_COLS, 1 / (canvas.height / C3D_CELL));
          this.mainCache.set(key, t);
        })
        .then(() => (this.attackCache.has(key) ? null : compositeAttackAtlas(a, weapon, shield)))
        .then((c) => {
          if (gen !== this.gen || !c || this.attackCache.has(key)) return;
          const at = new THREE.CanvasTexture(c);
          at.magFilter = THREE.NearestFilter; at.minFilter = THREE.NearestFilter; at.colorSpace = THREE.SRGBColorSpace;
          at.repeat.set(1 / C3D_ATTACK_COLS, 1 / 8);
          this.attackCache.set(key, at);
        })
        .then(() => (this.blockCache.has(key) ? null : compositeBlockAtlas(a, weapon, shield)))
        .then((c) => {
          if (gen !== this.gen || !c || this.blockCache.has(key)) return;
          const bt = new THREE.CanvasTexture(c);
          bt.magFilter = THREE.NearestFilter; bt.minFilter = THREE.NearestFilter; bt.colorSpace = THREE.SRGBColorSpace;
          bt.repeat.set(1, 1 / 8);
          this.blockCache.set(key, bt);
        })
        .finally(() => this.prewarming.delete(key));
    };
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(() => work());
    else setTimeout(work, 400);
  }

  /** Build the looping standing-smoke atlas from this character's composited idle frames (so the
   *  Blunt buff keeps their clothing/hair/skin) + a cigarette + rising smoke. `composited` is the
   *  full atlas just built in setAppearance — reused so we don't recomposite. */
  private buildSmokeAtlas(composited: HTMLCanvasElement): void {
    const gen = this.gen;
    void composeSmokeAtlas(composited).then((c) => {
      if (gen !== this.gen || !c) return;
      const st = new THREE.CanvasTexture(c);
      st.magFilter = THREE.NearestFilter; st.minFilter = THREE.NearestFilter;
      st.colorSpace = THREE.SRGBColorSpace;
      st.repeat.set(1 / C3D_SMOKE_COLS, 1 / 8);
      this.smokeTex?.dispose();
      this.smokeTex = st;
    });
  }

  private setMotionKey(moving: boolean, running: boolean): void {
    const key = running ? 2 : moving ? 1 : 0;
    if (key !== this.motionKey) { this.phase = 0; this.clock = 0; this.motionKey = key; }
    this.moving = moving;
    this.running = running;
  }

  /** Drive animation from an explicit 8-way facing (remote players). */
  setMotion(dir: Direction, moving: boolean, running = false): void {
    this.setMotionKey(moving, running);
    this.facing = dir;
    this.faceAngle = DIR_ANGLE[dir] ?? 0;
  }

  /** Drive animation from a live world heading (local player). */
  setHeading(angle: number, moving: boolean, running = false): void {
    this.setMotionKey(moving, running);
    this.faceAngle = angle;
  }

  setPosition(x: number, y: number, z: number): void {
    this.group.position.set(x, y, z);
  }

  /** Play the one-shot sword attack (16-frame layered atlas) once, overriding the normal body until
   *  it ends. No-op while dead, already mid-swing, or before the attack atlas has been built. */
  playAttack(): void {
    if (this.dead || this.attacking || !this.attackTex) return;
    this.attacking = true; this.attackPhase = 0; this.attackClock = 0;
    // scale the quad up for the larger attack cell, keeping the figure size + feet on the ground
    this.sprite.scale.set(ATTACK_SCALE, ATTACK_SCALE, 1);
    this.sprite.position.y = this.baseSpriteY * ATTACK_SCALE;
    // Apply the attack texture + frame-0 cell NOW (not next tick) so the just-scaled quad shows the
    // 216 attack cell immediately — otherwise one frame renders the 144 idle texture on the 1.5x quad,
    // flashing a giant figure. Uses the last camera yaw to pick the facing row.
    const row = this.rowForView(this.lastCamYaw);
    this.attackTex.offset.set(0, 1 - (row + 1) / 8);
    this.mat.map = this.attackTex;
    this.mat.needsUpdate = true;
  }

  /** True while the attack sheet is playing — callers hide their own held-weapon sprites then,
   *  since the sword/shield are baked into the sheet. */
  isAttacking(): boolean { return this.attacking; }

  private endAttack(): void {
    this.attacking = false;
    this.sprite.scale.set(1, 1, 1);              // restore the normal 144-cell quad
    this.sprite.position.y = this.baseSpriteY;
    if (this.tex) { this.mat.map = this.tex; this.mat.needsUpdate = true; }
  }

  /** Toggle the standing-smoke idle override (Blunt buff). While on AND standing still, the idle is
   *  replaced by the looping smoke sheet; walking/running/attacking still take over. Lazily loads the
   *  shared smoke sheet on first use. */
  setSmoking(on: boolean): void {
    if (on === this.smoking) return;
    this.smoking = on;
    if (on) { this.smokePhase = 0; this.smokeClock = 0; } // smokeTex is built per-appearance
    else if (this.tex && this.mat.map === this.smokeTex) {
      this.mat.map = this.tex; this.mat.needsUpdate = true; // restore the normal atlas
    }
  }

  /** Advance the active loop and repaint the cell, picking the row for the camera view. */
  tick(dtMs: number, camYaw: number): void {
    this.sprite.rotation.y = camYaw; // Y-billboard, stays upright
    this.lastCamYaw = camYaw; // remembered so playAttack can pick the facing row before the next tick
    this.applyHitFx(dtMs); // "got hit" red flash + shake, if active
    this.tickRod(dtMs, camYaw); // fishing-rod prop stance animation, if held
    if (this.bmx?.group.visible) { // BMX under the rider: yaw with the facing, wheels spin by speed
      this.bmx.group.rotation.y = this.faceAngle;
      this.bmx.tick(dtMs, this.bmxSpeed);
    }
    if (this.hitClock <= 0) dayNight.lightAt(this.group.position.x, this.group.position.z, this.mat.color); // day/night tint + nearby lamps
    if (this.fadeClock > 0) { // smooth fade-in after a teleport / zone change
      this.fadeClock = Math.max(0, this.fadeClock - dtMs);
      this.mat.opacity = 1 - this.fadeClock / this.fadeDur;
      if (this.fadeClock === 0) { this.mat.opacity = 1; this.mat.alphaTest = 0.5; }
    }
    if (this.healthHideIn > 0) { // hide the floating health bar 5s after the last hit
      this.healthHideIn = Math.max(0, this.healthHideIn - dtMs);
      if (this.healthHideIn === 0 && this.healthBar) this.healthBar.visible = false;
    }
    // One-shot attack overrides the walk/idle loop until its 16 frames have played.
    if (this.attacking && this.attackTex) {
      this.attackClock += dtMs;
      while (this.attackClock >= C3D_ATTACK_MS) {
        this.attackClock -= C3D_ATTACK_MS;
        if (++this.attackPhase >= C3D_ATTACK_FRAMES) { this.endAttack(); break; }
      }
      if (this.attacking) {
        if (this.mat.map !== this.attackTex) { this.mat.map = this.attackTex; this.mat.needsUpdate = true; }
        const row = this.rowForView(camYaw);
        this.attackTex.offset.set(this.attackPhase / C3D_ATTACK_COLS, 1 - (row + 1) / 8);
        return;
      }
    }
    // Held block stance (static, raised shield) — overrides idle/walk while blocking. Attack (above)
    // takes priority; released → setBlocking restores the normal atlas.
    if (this.blocking && this.blockTex && !this.dead) {
      if (this.mat.map !== this.blockTex) { this.mat.map = this.blockTex; this.mat.needsUpdate = true; }
      this.blockTex.offset.set(0, 1 - (this.rowForView(camYaw) + 1) / 8);
      return;
    }
    // Airborne jump pose (static) — overrides idle/walk while the jump arc plays (the billboard's Y is
    // hopped by the caller). Attack/block above take priority. Only when the jump art is loaded.
    if (this.jumping && this.jumpTex && !this.dead) {
      if (this.mat.map !== this.jumpTex) { this.mat.map = this.jumpTex; this.mat.needsUpdate = true; }
      this.jumpTex.offset.set(0, 1 - (this.rowForView(camYaw) + 1) / 8);
      return;
    }
    if (this.mat.map === this.jumpTex && this.jumpTex && this.tex) { this.mat.map = this.tex; this.mat.needsUpdate = true; }
    // Seated sit pose (static) — overrides idle while seated at a casino table. Only when the sit art
    // is loaded (else sitTex is null and we fall through to the perched idle pose).
    if (this.sitting && this.sitTex && !this.dead) {
      if (this.mat.map !== this.sitTex) { this.mat.map = this.sitTex; this.mat.needsUpdate = true; }
      this.sitTex.offset.set(0, 1 - (this.rowForView(camYaw) + 1) / 8);
      return;
    }
    if (this.mat.map === this.sitTex && this.sitTex && this.tex) { this.mat.map = this.tex; this.mat.needsUpdate = true; }
    // Looping standing-smoke overrides the IDLE pose while the Blunt buff is active and the player is
    // standing still. Walking/running/attacking fall through to the normal loop.
    if (this.smoking && this.smokeTex && !this.moving && !this.running && !this.dead) {
      this.smokeClock += dtMs;
      while (this.smokeClock >= C3D_SMOKE_MS) { this.smokeClock -= C3D_SMOKE_MS; this.smokePhase = (this.smokePhase + 1) % C3D_SMOKE_FRAMES; }
      if (this.mat.map !== this.smokeTex) { this.mat.map = this.smokeTex; this.mat.needsUpdate = true; }
      const row = this.rowForView(camYaw);
      this.smokeTex.offset.set(this.smokePhase / C3D_SMOKE_COLS, 1 - (row + 1) / 8);
      return;
    }
    // Smoke texture still bound (e.g. moving while buffed, or just stopped) → restore the body atlas.
    if (this.mat.map === this.smokeTex && this.tex) { this.mat.map = this.tex; this.mat.needsUpdate = true; }
    const stepMs = this.running ? C3D_RUN_MS : this.moving ? C3D_WALK_MS : C3D_IDLE_MS;
    const count = this.running ? C3D_RUN_FRAMES : this.moving ? C3D_WALK_FRAMES : C3D_IDLE_FRAMES;
    this.clock += dtMs;
    while (this.clock >= stepMs) { this.clock -= stepMs; this.phase = (this.phase + 1) % count; }
    this.applyFrame(camYaw);
  }

  private applyFrame(camYaw = 0): void {
    if (!this.tex) return;
    const row = this.rowForView(camYaw);
    // Resolve the absolute cell index for this view-row + animation phase.
    const idx = this.running ? c3dRunFrame(ROW_DIR[row], this.phase)
      : this.moving ? c3dWalkFrame(ROW_DIR[row], this.phase) : c3dIdleFrame(ROW_DIR[row], this.phase);
    const col = idx % C3D_COLS;
    const sheetRow = Math.floor(idx / C3D_COLS);
    const rows = 1 / this.tex.repeat.y;
    this.tex.offset.set(col / C3D_COLS, 1 - (sheetRow + 1) / rows);
  }

  /** Facing relative to the camera → 0=toward viewer, stepping 45° (spike-verified). */
  private rowForView(camYaw: number): number {
    let rel = this.faceAngle - camYaw;
    rel = ((rel % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return Math.round(rel / (Math.PI / 4)) % 8;
  }

  dispose(): void {
    this.gen++;
    // tex/attackTex/blockTex point into the caches — dispose via the caches (avoids double-dispose).
    this.clearAtlasCache();
    this.smokeTex?.dispose();
    this.mat.dispose();
    (this.sprite.geometry as THREE.BufferGeometry).dispose();
    this.healthTex?.dispose();
    if (this.healthBar) (this.healthBar.material as THREE.SpriteMaterial).dispose();
    if (this.skullSprite) (this.skullSprite.material as THREE.SpriteMaterial).dispose(); // shared skull texture is NOT disposed here
  }
}

/** Shared pixel-art skull texture for the red-named PvP marker (loaded once, reused by every billboard).
 *  Transparent-background PNG at /assets/ui/pvp-skull.png, sampled with NearestFilter for the pixel look. */
let SKULL_TEX: THREE.Texture | null = null;
function skullTexture(): THREE.Texture {
  if (!SKULL_TEX) {
    SKULL_TEX = new THREE.TextureLoader().load(assetUrl("/assets/ui/pvp-skull.png"));
    SKULL_TEX.magFilter = SKULL_TEX.minFilter = THREE.NearestFilter;
    SKULL_TEX.colorSpace = THREE.SRGBColorSpace;
  }
  return SKULL_TEX;
}

/** View-row index → the sheet's facing key (inverse of the render's ring order). */
const ROW_DIR: Direction[] = [
  Direction.Down, Direction.DownRight, Direction.Right, Direction.UpRight,
  Direction.Up, Direction.UpLeft, Direction.Left, Direction.DownLeft,
];

/** 8-octant table for quantising a world heading into a `dir` to send to the server. */
export const DIR_BY_OCTANT: Direction[] = ROW_DIR;
export function headingToDir(angle: number): Direction {
  const a = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  return DIR_BY_OCTANT[Math.round(a / (Math.PI / 4)) % 8];
}
